import React, { useEffect, useRef, useState } from 'react';
import { Link } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

// Vendored hls.js — keep this version in lockstep with WebinarRoom.jsx.
const HLS_JS_SRC = '/vendor/hls-1.5.13.min.js';

let hlsLoaderPromise = null;
function loadHlsJs() {
  if (window.Hls) return Promise.resolve(window.Hls);
  if (hlsLoaderPromise) return hlsLoaderPromise;
  hlsLoaderPromise = new Promise((resolve, reject) => {
    const tag = document.createElement('script');
    tag.src = HLS_JS_SRC;
    tag.async = true;
    tag.onload = () => resolve(window.Hls);
    tag.onerror = () => reject(new Error('Failed to load hls.js'));
    document.head.appendChild(tag);
  });
  return hlsLoaderPromise;
}

function fmtDuration(seconds) {
  if (!seconds || seconds <= 0) return '—';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}h ${m.toString().padStart(2, '0')}m ${s.toString().padStart(2, '0')}s`
    : `${m}m ${s.toString().padStart(2, '0')}s`;
}

function fmtBytes(n) {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function fmtTs(s) {
  return s ? new Date(s).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  }) : '—';
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
      <p className="mt-1 text-xl font-bold text-gray-900 tabular-nums">{value}</p>
    </div>
  );
}

function Player({ manifestUrl }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const video = videoRef.current;
    if (!video) return;

    const start = async () => {
      try {
        const canNative = video.canPlayType('application/vnd.apple.mpegurl') === 'probably';
        if (canNative) {
          video.src = manifestUrl;
          return;
        }
        const Hls = await loadHlsJs();
        if (cancelled) return;
        if (!Hls.isSupported()) {
          setError('Your browser does not support HLS playback.');
          return;
        }
        const hls = new Hls({ enableWorker: true });
        hlsRef.current = hls;
        hls.attachMedia(video);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(manifestUrl));
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (!data.fatal) return;
          setError('Playback failed. Refresh the page to retry (SAS URLs may have expired).');
        });
      } catch (e) {
        setError(e?.message || 'Failed to start playback.');
      }
    };
    start();

    return () => {
      cancelled = true;
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (video) {
        video.removeAttribute('src');
        video.load();
      }
    };
  }, [manifestUrl]);

  return (
    <div className="rounded-2xl overflow-hidden border border-gray-200 bg-black shadow-sm">
      <video
        ref={videoRef}
        controls
        playsInline
        preload="metadata"
        className="w-full bg-black"
        style={{ aspectRatio: '16 / 9' }}
      />
      {error && <p className="px-4 py-2 text-xs text-rose-300 bg-rose-900/60">{error}</p>}
    </div>
  );
}

export default function SuperadminBroadcastRecording({ webinar, recording }) {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4">
        <div>
          <Link href={`/superadmin/webinars/${webinar.id}`} className="text-sm text-indigo-600 hover:text-indigo-700">
            ← Back to webinar
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-gray-900">Broadcast recording</h1>
          <p className="text-sm text-gray-600">{webinar.title}</p>
        </div>

        {recording ? (
          <>
            <Player manifestUrl={recording.manifest_url} />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Duration"  value={fmtDuration(recording.total_duration_seconds)} />
              <Stat label="Segments"  value={recording.segment_count} />
              <Stat label="Size"      value={fmtBytes(recording.total_byte_size)} />
              <Stat label="Recorded"  value={fmtTs(recording.finalized_at)} />
            </div>

            <p className="text-xs text-gray-500">
              Playback URLs are signed for 1 hour. If playback fails after a long pause, refresh this page for fresh credentials.
            </p>
          </>
        ) : (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">
            No broadcast recording stored for this webinar yet.
          </div>
        )}
      </div>
    </Layout>
  );
}
